/*
 * Kachel-Protokoll einer Buehnen-Browserkachel (Regeln und Seiten-Skripte in
 * shared/kachel-protokoll.ts, Speicher in server/lib/kachel-protokoll.ts).
 *
 * Nur Electron und nur Kacheln `#guide-p-browser-…`: Workspace-Browser bleiben unprotokolliert.
 * Gelauscht wird wie bei der Vorbefuellung in der CAPTURE-Phase am Panel-Container — dort
 * kommen dom-ready/did-navigate jeder Webview darunter vorbei, auch wenn sie nach einem
 * Geraetewechsel neu entsteht. Ziel jedes Aufrufs ist die Webview, die das Ereignis meldete.
 *
 * Was hier entsteht, geht NUR an /api/kachel-protokoll — nie in console.*, nie in Telemetrie.
 */
import { useEffect, useRef, type RefObject } from 'react';
import {
  TEXTPROBE_NACH_MS, TEXTPROBE_SKRIPT, baueWaechterSkript, istProtokollKachel, normalisiereKachelId,
  type ProtokollEintrag,
} from '../../../shared/kachel-protokoll';

type Webview = HTMLElement & { executeJavaScript?: (code: string) => Promise<unknown>; getURL?: () => string; getTitle?: () => string };

/** Pause, bevor eine Navigation festgehalten wird: Weiterleitungsketten fallen so auf ihr Ziel zusammen. */
const SEITE_RUHE_MS = 1_500;
const NEUVERSUCH_MS = 5_000;
const MAX_WARTESCHLANGE = 300;

export function useKachelProtokoll(panelId: string | undefined, isElectron: boolean, containerRef: RefObject<HTMLDivElement | null>): void {
  const aktiv = isElectron && istProtokollKachel(panelId);
  const ausgang = useRef<ProtokollEintrag[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!aktiv || !el || !panelId) return;
    const kachel = normalisiereKachelId(panelId);
    const schluessel = `__cuiKp_${Math.random().toString(36).slice(2, 10)}`;
    let lebt = true;
    let generation = 0;
    let sendet = false;
    let letzteSeite = '';
    const timer = new Set<ReturnType<typeof setTimeout>>();
    const spaeter = (fn: () => void, ms: number) => {
      const t = setTimeout(() => { timer.delete(t); fn(); }, ms);
      timer.add(t);
    };

    const sende = async () => {
      if (sendet || !ausgang.current.length) return;
      sendet = true;
      const stapel = ausgang.current.slice(0, 100);
      try {
        const r = await fetch(`/api/kachel-protokoll/kachel/${encodeURIComponent(kachel)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eintraege: stapel }),
        });
        if (r.ok) ausgang.current.splice(0, stapel.length);
        else if (r.status === 400) {
          // Ein ungueltiger Stapel wird durch Wiederholen nicht gueltig. Laut melden, nicht schweigend stauen.
          ausgang.current.splice(0, stapel.length);
          const d = await r.json().catch(() => ({}));
          console.error(`[kachel-protokoll] ${kachel}: Server lehnt ${stapel.length} Einträge ab`, d.wo ?? d.error);
        }
      } catch { /* Server nicht erreichbar (Neustart) — der Neuversuch unten holt es nach */ }
      sendet = false;
      if (lebt && ausgang.current.length) spaeter(sende, NEUVERSUCH_MS);
    };
    const merke = (e: ProtokollEintrag[]) => {
      ausgang.current.push(...e);
      if (ausgang.current.length > MAX_WARTESCHLANGE) ausgang.current.splice(0, ausgang.current.length - MAX_WARTESCHLANGE);
      void sende();
    };

    const probe = (wv: Webview, zu: string) => {
      for (const ms of TEXTPROBE_NACH_MS) spaeter(async () => {
        try {
          const r = await wv.executeJavaScript?.(TEXTPROBE_SKRIPT) as Omit<Extract<ProtokollEintrag, { art: 'textprobe' }>, 'art' | 'at' | 'zu'> | undefined;
          if (r) merke([{ art: 'textprobe', at: new Date().toISOString(), zu, ...r }]);
        } catch { /* Webview weg oder Seite im Wechsel — die zweite Probe versucht es erneut */ }
      }, ms);
    };

    // Long-Poll: ein Abruf je Dokument-Generation. Ein neues dom-ready beendet die alte Schleife.
    const lausche = async (wv: Webview) => {
      const meine = ++generation;
      const skript = baueWaechterSkript(schluessel);
      while (lebt && meine === generation) {
        let r: unknown;
        try { r = await wv.executeJavaScript?.(skript); }
        catch { return; }
        if (!lebt || meine !== generation) return;
        const liste = Array.isArray(r) ? r as ProtokollEintrag[] : [];
        if (!liste.length) continue;
        merke(liste);
        for (const e of liste) if (e.art === 'absendung' || e.art === 'klick') probe(wv, e.id);
      }
    };

    const seite = (wv: Webview) => {
      spaeter(() => {
        const url = wv.getURL?.() ?? '';
        if (!url || url === letzteSeite || url === 'about:blank') return;
        letzteSeite = url;
        merke([{ art: 'seite', at: new Date().toISOString(), url: url.slice(0, 500), titel: (wv.getTitle?.() ?? '').slice(0, 200) }]);
      }, SEITE_RUHE_MS);
    };

    const ereignis = (e: Event) => {
      const wv = e.target as Webview | null;
      if (wv?.tagName !== 'WEBVIEW') return;
      if (e.type === 'dom-ready') void lausche(wv);
      else if (e.type === 'did-navigate-in-page' && (e as Event & { isMainFrame?: boolean }).isMainFrame === false) return;
      else seite(wv);
    };
    const arten = ['dom-ready', 'did-navigate', 'did-navigate-in-page'];
    for (const a of arten) el.addEventListener(a, ereignis, true);
    // Montiert die Kachel erst NACH dem ersten dom-ready (Wiederanheften), sofort einsteigen.
    const vorhanden = el.querySelector('webview') as Webview | null;
    if (vorhanden) spaeter(() => { if (generation === 0) { void lausche(vorhanden); seite(vorhanden); } }, 2_000);

    return () => {
      lebt = false;
      for (const a of arten) el.removeEventListener(a, ereignis, true);
      timer.forEach(clearTimeout);
      void sende();
    };
  }, [aktiv, panelId, containerRef]);
}
